import React from "react";
import { Box, Drawer, Typography } from "@mui/material";

import { SidebarSectionType } from "../../types/sidebar";
import BrandLogo from "../shell/BrandLogo";
import { TOPBAR_HEIGHT } from "../shell/layout";
import SidebarNav from "./SidebarNav";

export type SidebarProps = {
  items: SidebarSectionType[];
  /** permanent: docked beside the page (md+); temporary: slide-over drawer (below md). */
  variant: "permanent" | "temporary";
  /** Width in px (212 expanded, 64 collapsed). */
  width: number;
  /** Icons only, captions hidden (permanent variant). */
  collapsed?: boolean;
  /** Temporary variant: whether the drawer is open. */
  open?: boolean;
  /** Temporary variant: close request (backdrop, Esc, or a nav click). */
  onClose?: () => void;
  /** id of the nav element (the hamburger's aria-controls). */
  navId?: string;
};

const widthTransition = "width 0.2s ease-in-out";

/** The left navigation: captioned sections on the paper surface with a right divider. */
const Sidebar: React.FC<SidebarProps> = ({
  items,
  variant,
  width,
  collapsed = false,
  open = false,
  onClose,
  navId,
}) => {
  if (variant === "temporary") {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        // Above the top bar (drawer + 1): the drawer's own logo row shows, and its backdrop
        // covers the bar so the account button can't be clicked under the open drawer.
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
        PaperProps={{
          sx: {
            width,
            bgcolor: "background.paper",
            borderRight: 1,
            borderColor: "divider",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            px: 4,
            minHeight: TOPBAR_HEIGHT,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <BrandLogo height={22} />
        </Box>
        <Typography
          component="div"
          sx={{ px: 4.5, pt: 3, fontSize: "0.9375rem", fontWeight: 500 }}
        >
          CSI Pay
        </Typography>
        <SidebarNav items={items} id={navId} onNavigate={onClose} />
      </Drawer>
    );
  }

  // The docked root stretches to the height of the row beside the page, so it draws the
  // column's surface and divider all the way down. The paper only holds the nav: it sticks
  // under the top bar, is as tall as the nav and never taller than the space below the bar,
  // so the sidebar adds no page scroll with or without the environment banner.
  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        boxSizing: "border-box",
        bgcolor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
        transition: widthTransition,
        "& .MuiDrawer-paper": {
          width: "100%",
          position: "sticky",
          top: TOPBAR_HEIGHT,
          height: "auto",
          maxHeight: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
          boxSizing: "border-box",
          overflowX: "hidden",
          bgcolor: "background.paper",
          border: 0,
        },
      }}
    >
      <SidebarNav items={items} collapsed={collapsed} id={navId} />
    </Drawer>
  );
};

export default Sidebar;
